// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { Check, Loader2, Mail, Phone, Shield } from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import { tenantSettingsService } from "../../services/tenantSettingsService";
import { usePermissions } from "../../hooks/usePermissions";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess, hasAdminAccess } from "../../config/roleConfig";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import PageLoader from "../../components/PageLoader";
import useTenants from "../../hooks/useTenants";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import { emitTenantSettingsUpdated } from "../../utils/tenantSettingsEvents";

const BrandingKontaktSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({});
  const [logoUrl, setLogoUrl] = useState(null);
  const [email, setEmail] = useState({});
  const [contact, setContact] = useState({});
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const { userRoles, userInfo } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);
  const isAdmin = hasAdminAccess(userRoles || []);
  let selectednewTenant = null;
  if (isAdmin) {
    selectednewTenant = userInfo.tenant_id || null;
  }
  const {
    tenantOptions,
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
  } = useTenants({
    ...getAdminTenantConfig(isSuperAdmin),
    includeAll: false,
  });

  const loadSettings = async () => {
    try {
      setLoading(true);
      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        setBranding({});
        setEmail({});
        setContact({});
        setLogoUrl(null);
        return;
      }
      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        setBranding({});
        setEmail({});
        setContact({});
        setLogoUrl(null);
        return;
      }
      const settings = await tenantSettingsService.getTenantConfig(tenantId);
      setBranding(settings.branding || {});
      setEmail(settings.email || {});
      setContact(settings.contact || {});

      // Logo lives in the picture API (keyed by tenant_id); fetch it as an
      // authenticated blob URL for display.
      const url = await tenantSettingsService.getTenantLogoUrl(tenantId);
      setLogoUrl(url);
    } catch (error) {
      console.error("Error loading settings:", error);
      showError("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [selectedTenant, isSuperAdmin]);

  // Revoke the previous blob URL whenever it changes or the page unmounts.
  useEffect(() => {
    return () => {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
    };
  }, [logoUrl]);

  const handleBrandingChange = (e) => {
    const { name, value, type, files } = e.target;
    setBranding((prev) => ({
      ...prev,
      [name]: type === "file" ? files?.[0] : value,
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        showError("Bitte zuerst einen Mandanten auswählen");
        return;
      }

      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        showError("Kein Mandant für das Hochladen gefunden");
        return;
      }

      await tenantSettingsService.uploadTenantLogo(tenantId, file);

      // Re-fetch the freshly uploaded logo for immediate display.
      const url = await tenantSettingsService.getTenantLogoUrl(tenantId);
      setLogoUrl(url);
      emitTenantSettingsUpdated(tenantId);
      showSuccess("Logo erfolgreich hochgeladen");
    } catch (error) {
      console.error("Error uploading logo:", error);
      showError("Fehler beim Hochladen des Logos");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        showError("Bitte zuerst einen Mandanten auswählen");
        return;
      }
      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        showError("Kein Mandant für das Speichern gefunden");
        return;
      }
      const currentConfig =
        await tenantSettingsService.getTenantConfig(tenantId);
      const nextConfig = {
        ...currentConfig,
        branding,
        email,
        contact,
      };
      await tenantSettingsService.updateTenantConfig(tenantId, nextConfig);
      emitTenantSettingsUpdated(tenantId);
      showSuccess("Branding & Kontakt-Einstellungen erfolgreich gespeichert!");
      await loadSettings();
    } catch (error) {
      console.error("Error saving settings:", error);
      showError("Fehler beim Speichern der Einstellungen");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    loadSettings();
  };

  if (
    !isSuperAdmin &&
    !isAdmin &&
    !hasPermission("tenant_settings.manage") &&
    !hasPermission("tenant_settings.view")
  ) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-slate-400">
            Keine Berechtigung für diese Einstellungen
          </p>
        </div>
      </div>
    );
  }
  const inputClass =
    "w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:placeholder-slate-400 text-gray-900 dark:text-slate-100";
  const labelClass =
    "block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="Branding & Kontakt"
        subtitle="Markenidentität verwalten"
        tenantOptions={isSuperAdmin ? tenantOptions : []}
        selectedTenant={isSuperAdmin ? selectedTenant : null}
        onTenantChange={isSuperAdmin ? setSelectedTenant : undefined}
        tenantsLoading={isSuperAdmin ? tenantsLoading : false}
      />
      <div className="mx-auto px-4 lg:px-6 sm:mt-6 mt-3 pb-8">
        {loading ? (
          <PageLoader message="Lade Einstellungen..." />
        ) : (
          <form onSubmit={handleSubmit} className="w-full">
            {/* Markenidentität */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 sm:p-8 mb-6">
              <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-6">
                Markenidentität
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="company_name" className={labelClass}>
                      Firmenname
                    </label>
                    <input
                      type="text"
                      id="company_name"
                      name="company_name"
                      value={branding.company_name || ""}
                      onChange={handleBrandingChange}
                      className={inputClass}
                      placeholder="z.B. Stadtplatz Berlin"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Primärfarbe</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="primary_color"
                        name="primary_color"
                        value={branding.primary_color || "#6366F1"}
                        onChange={handleBrandingChange}
                        className="h-10 w-14 rounded-lg cursor-pointer border border-gray-200 dark:border-slate-600 bg-transparent"
                      />
                      <input
                        type="text"
                        value={branding.primary_color || "#6366F1"}
                        onChange={(e) =>
                          setBranding((p) => ({
                            ...p,
                            primary_color: e.target.value,
                          }))
                        }
                        className={`${inputClass} flex-1 font-mono text-sm`}
                        placeholder="#6366F1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className={labelClass}>Logo</label>
                    {logoUrl ? (
                      <div className="flex items-center gap-4">
                        <div className="h-20 w-20 rounded-lg overflow-hidden border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 flex items-center justify-center">
                          <img
                            src={logoUrl}
                            alt="Tenant Logo"
                            className="max-h-25 max-w-full object-contain"
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        Noch kein Logo hochgeladen.
                      </p>
                    )}
                    <div>
                      <label htmlFor="logo_file" className={labelClass}>
                        Logo hochladen
                      </label>
                      <input
                        type="file"
                        id="logo_file"
                        name="logo_file"
                        onChange={handleLogoUpload}
                        className={inputClass}
                        accept="image/*"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="tagline" className={labelClass}>
                      Tagline
                    </label>
                    <input
                      type="text"
                      id="tagline"
                      name="tagline"
                      value={branding.tagline || ""}
                      onChange={handleBrandingChange}
                      className={inputClass}
                      placeholder="z.B. Öffentliche Räume fair verwalten"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Sekundärfarbe</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="secondary_color"
                        name="secondary_color"
                        value={branding.secondary_color || "#06B6D4"}
                        onChange={handleBrandingChange}
                        className="h-10 w-14 rounded-lg cursor-pointer border border-gray-200 dark:border-slate-600 bg-transparent"
                      />
                      <input
                        type="text"
                        value={branding.secondary_color || "#06B6D4"}
                        onChange={(e) =>
                          setBranding((p) => ({
                            ...p,
                            secondary_color: e.target.value,
                          }))
                        }
                        className={`${inputClass} flex-1 font-mono text-sm`}
                        placeholder="#06B6D4"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Kontaktinformationen */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 sm:p-8 mb-6">
              <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-6">
                Kontaktinformationen
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>
                      <Mail className="w-4 h-4 inline-block mr-1.5 align-middle text-gray-500 dark:text-slate-400" />
                      E-Mail
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contact.email || ""}
                      onChange={(e) =>
                        setContact((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className={inputClass}
                      placeholder="info@beispiel.de"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>
                      <Phone className="w-4 h-4 inline-block mr-1.5 align-middle text-gray-500 dark:text-slate-400" />
                      Telefon
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={contact.phone || ""}
                      onChange={(e) =>
                        setContact((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      className={inputClass}
                      placeholder="+49 30 12345678"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-5 py-2.5 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary-color dark:bg-primary-color/40 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
                {saving ? "Speichern..." : "Speichern"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BrandingKontaktSettings;
